import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormSelect,
  CFormInput,
} from "@coreui/react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  restoreProducts,
  trashedProducts,
} from "../../features/seller/sellerSlice";
import { timeAgo } from "../../utils/timeAgo";
import RestoreConfirmationModal from "../../components/seller/RestoreConfirmationModal";
import AppLoader from "../../components/seller/AppLoader";
import AppToast from "../../components/seller/AppToast";

const ViewTrash = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list: products, loading } = useSelector(
    (state) => state.seller.trashedProducts
  );
  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [productToRestore, setProductToRestore] = useState(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");
  // console.log("List:", products);

  useEffect(() => {
    dispatch(trashedProducts())
      .unwrap()
      .catch((error) => {
        console.error("Failed to load trashed products", error);
        // Show toast or fallback UI
      });
  }, [dispatch]);

  const handleRestore = async (product) => {
    try {
      const res = await dispatch(
        restoreProducts({ id: product.id, data: {} })
      ).unwrap();
      setShowRestoreModal(false);
      setProductToRestore(null);
      // Optionally show a toast here
      setToastType("success");
      setToastMessage(res?.message || "Product restored successfully.");
      setToastVisible(true);
    } catch (err) {
      console.error("Failed to restore product:", err);
      setToastType("error");
      setToastMessage(err?.message || "Failed to restore product.");
      setToastVisible(true);
    }
  };

  const filtered = products.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / entriesPerPage);

  if (loading) {
    return <AppLoader />;
  }
  return (
    <>
      <div className="container-fluid px-3">
        <CRow>
          <CCol>
            <h4 className="mb-3">Trashed Products</h4>
          </CCol>
          <CCol className="text-end">
            <CButton color="secondary" onClick={() => navigate(-1)}>
              Back
            </CButton>
          </CCol>
        </CRow>

        <CCard className="mt-3">
          <CCardHeader>
            <CRow className="align-items-center">
              <CCol md={6}>
                Show{" "}
                <CFormSelect
                  size="sm"
                  style={{ display: "inline-block", width: "auto" }}
                  value={entriesPerPage}
                  onChange={(e) => {
                    setEntriesPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  options={[
                    { label: "5", value: "5" },
                    { label: "10", value: "10" },
                    { label: "25", value: "25" },
                  ]}
                />{" "}
                entries
              </CCol>
              <CCol md={6} className="text-end">
                Search:{" "}
                <CFormInput
                  type="text"
                  size="sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ display: "inline-block", width: "200px" }}
                />
              </CCol>
            </CRow>
          </CCardHeader>

          <CCardBody>
            <CTable responsive bordered hover>
              <CTableHead color="light">
                <CTableRow>
                  <CTableHeaderCell>#</CTableHeaderCell>
                  <CTableHeaderCell>Name</CTableHeaderCell>
                  <CTableHeaderCell>Category</CTableHeaderCell>
                  <CTableHeaderCell>Price</CTableHeaderCell>
                  <CTableHeaderCell>Deleted At</CTableHeaderCell>
                  <CTableHeaderCell>Actions</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {currentData.length === 0 ? (
                  <CTableRow>
                    <CTableDataCell
                      colSpan={6}
                      className="text-center text-muted"
                    >
                      Trash is empty
                    </CTableDataCell>
                  </CTableRow>
                ) : (
                  currentData.map((product, index) => (
                    <CTableRow key={product.id}>
                      <CTableDataCell>
                        {indexOfFirst + index + 1}
                      </CTableDataCell>
                      <CTableDataCell>{product.name || "-"}</CTableDataCell>
                      <CTableDataCell>
                        {product.category_name || "-"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {product.price != null ? `$${product.price}` : "-"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {product.deleted_at ? timeAgo(product.deleted_at) : "-"}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CButton
                          size="sm"
                          color="success"
                          onClick={() => {
                            setProductToRestore(product);
                            setShowRestoreModal(true);
                          }}
                        >
                          Restore
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  ))
                )}
              </CTableBody>
            </CTable>

            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing {indexOfFirst + 1} to{" "}
                {Math.min(indexOfLast, filtered.length)} of {filtered.length}{" "}
                entries
              </div>
              <div className="d-flex align-items-center">
                <CButton
                  size="sm"
                  color="light"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="me-2"
                >
                  Prev
                </CButton>

                {[...Array(totalPages)].map((_, i) => (
                  <CButton
                    key={i}
                    size="sm"
                    color={currentPage === i + 1 ? "primary" : "light"}
                    onClick={() => setCurrentPage(i + 1)}
                    className="me-2"
                  >
                    {i + 1}
                  </CButton>
                ))}

                <CButton
                  size="sm"
                  color="light"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </CButton>
              </div>
            </div>
          </CCardBody>
        </CCard>

        {/* Restore Confirmation Modal */}
        <RestoreConfirmationModal
          visible={showRestoreModal}
          onClose={() => setShowRestoreModal(false)}
          onConfirm={() => handleRestore(productToRestore)}
          itemName={productToRestore?.name}
        />
      </div>
      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
      />
    </>
  );
};

export default ViewTrash;
